import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  Alert,
  FormControlLabel,
  Checkbox,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import SearchIcon from "@mui/icons-material/Search";
import SortIcon from "@mui/icons-material/Sort";
import { useTranslation } from "react-i18next";
import { getLiveTrainingPortalRoot } from "../hooks/useLiveFullscreen";
import { useTheme, alpha } from "@mui/material/styles";
import { usePermissions } from "../context/Permissioncontext";
import {
  grpplayerlist,
  playerpresent,
  groupdetails,
  inviteTrainingSessionGuest,
  createClubPlayerInvite,
  addDemoSessionPlayer,
  playeradtable,
  tablelist,
  recordNewcomerPayment,
  startNewcomerCardPayment,
} from "./funtionsBD/Mainservice";
import { AddNewcomerDialog } from "./newcomer/AddNewcomerDialog";
import {
  playerPresenceDisplayName,
  playerPresenceInitials,
} from "../utils/liveTrainingPlayerContext";
import { isSessionClosedForPresence } from "../utils/sessionPresenceEdit";
import { filterTablesForStation } from "../utils/coachSessionTables";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useBrand } from "../context/BrandContext";
import { isDemoClub } from "../utils/demoClub";
import { readStoredUser } from "../utils/homeRole";
import { DemoGuestInviteCollapsible } from "./DemoPhoneJoinLink";
import "./PlayerPresenceSheet.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function resolvePresenceGroupId(selectedSession, selectedgrp) {
  return (
    selectedSession?.gr_id ||
    selectedgrp?.groupDetails?.gr_id ||
    selectedgrp?.gr_id ||
    null
  );
}

function isGuestPlayer(player) {
  return (
    player?.is_guest === true ||
    (player?.isActive === false && !!player?.user_email)
  );
}

function shortName(p) {
  return playerPresenceDisplayName(p, "—");
}

function tableEligibleForThirdPlayer(table) {
  const p1 = table?.player1;
  const p2 = table?.player2;
  const p3 = table?.player3;
  const has1 =
    p1 &&
    typeof p1 === "object" &&
    Object.keys(p1).length > 0 &&
    (p1.player_id || p1.user_first_name || p1.user_alias);
  const has2 =
    p2 &&
    typeof p2 === "object" &&
    Object.keys(p2).length > 0 &&
    (p2.player_id || p2.user_first_name || p2.user_alias);
  const has3 =
    p3 &&
    typeof p3 === "object" &&
    Object.keys(p3).length > 0 &&
    (p3.player_id || p3.user_first_name || p3.user_alias);
  return !!has1 && !!has2 && !has3;
}

function formatTableSummaryLine(table) {
  const n = table.table_number ?? "?";
  const a = shortName(table.player1);
  const b = shortName(table.player2);
  return `T${n} · ${a} · ${b}`;
}

function playerMatchesQuery(player, qLower) {
  if (!qLower) return true;
  const hay = [
    player.user_alias,
    player.user_first_name,
    player.user_last_name,
    [player.user_first_name, player.user_last_name].filter(Boolean).join(" "),
    player.player_id,
    player.user_email,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(qLower);
}

function playerPresenceClassement(player) {
  const raw = player?.classement || player?.fltt_classement || "";
  return String(raw).trim();
}

function playerSortNameParts(player) {
  return {
    last: String(player?.user_last_name || "").trim().toLowerCase(),
    first: String(player?.user_first_name || "").trim().toLowerCase(),
    alias: String(player?.user_alias || "").trim().toLowerCase(),
  };
}

/** Sort presence roster by classement and/or name — never filters the list. */
function comparePlayersBySort(a, b, sortBy = "classement") {
  if (sortBy === "classement") {
    const ca = playerPresenceClassement(a);
    const cb = playerPresenceClassement(b);
    if (ca && cb) {
      const byLevel = ca.localeCompare(cb, undefined, {
        numeric: true,
        sensitivity: "base",
      });
      if (byLevel !== 0) return byLevel;
    } else if (ca && !cb) return -1;
    else if (!ca && cb) return 1;
  }

  const na = playerSortNameParts(a);
  const nb = playerSortNameParts(b);
  if (sortBy === "firstName") {
    const byFirst = na.first.localeCompare(nb.first) || na.last.localeCompare(nb.last);
    if (byFirst !== 0) return byFirst;
  } else {
    // "lastName" primary, or secondary after classement
    const byLast = na.last.localeCompare(nb.last) || na.first.localeCompare(nb.first);
    if (byLast !== 0) return byLast;
  }
  return na.alias.localeCompare(nb.alias);
}

function coachMatchesQuery(coach, qLower) {
  if (!qLower) return true;
  const coachUser = coach.tbl_user || coach.user || {};
  const hay = [
    coachUser.user_alias,
    coachUser.user_first_name,
    coachUser.user_last_name,
    [coachUser.user_first_name, coachUser.user_last_name].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(qLower);
}

function presenceStatBoxSx(theme, paletteKey) {
  const main = theme.palette[paletteKey].main;
  return {
    textAlign: "center",
    p: 2,
    backgroundColor: alpha(main, theme.palette.mode === "dark" ? 0.18 : 0.12),
    borderRadius: 1,
    border: `1px solid ${alpha(main, theme.palette.mode === "dark" ? 0.35 : 0.28)}`,
  };
}

const PlayerPresenceDialog = ({
  open,
  onClose,
  selectedSession,
  selectedgrp,
  onUpdate,
  /** Training room: current exercise + tables so coach can assign a 3rd player from this dialog */
  trainingTables,
  trainingSelectedExercise,
  /** Focused Live Training station — seats/third-player tables stay on this station. */
  tsgId = null,
  stations = null,
  onAfterThirdPlayerTableAssign,
  /** Called when coach presses Done/Close after presence changes (live training). */
  onDone,
  /** Called right after a player is marked present (live training auto-seat). */
  onPlayerMarkedPresent,
  zIndex = null,
  /**
   * Optional portal mount (defaults to Live Training `.lt-page` root so the
   * sheet stays visible above chrono/count panels and during page fullscreen).
   */
  portalRoot = null,
  /** "dialog" (default) or "sheet" — bottom slide-up panel for live training */
  presentation = "dialog",
  isLandscape = false,
  /** Force correction mode (finalized session) even if session flags are stale. */
  presenceCorrectionOnly = false,
  /**
   * Training-session "correct presence" still uses the live-training add-player
   * sheet. Keep invite/add available after the session is finalized.
   */
  allowInviteWhenClosed = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { hasPermission } = usePermissions();
  const { brand, themeMode } = useBrand();
  const sheetThemeClass = themeMode === "light" ? "light-mode" : "dark-mode";
  const signedInUser = readStoredUser();
  const [groupIsDemo, setGroupIsDemo] = useState(false);
  const demoClub = isDemoClub(
    brand,
    {
      ...(selectedgrp?.groupDetails || {}),
      ...(groupIsDemo ? { is_demo: true } : {}),
    },
    signedInUser
  );
  const [allPlayers, setAllPlayers] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [inviteFirstName, setInviteFirstName] = useState("");
  const [inviteLastName, setInviteLastName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [invitePseudo, setInvitePseudo] = useState("");
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  const [inviteExpanded, setInviteExpanded] = useState(false);
  const [newcomerOpen, setNewcomerOpen] = useState(false);
  const [paymentGate, setPaymentGate] = useState(null);
  const [resolvedClubId, setResolvedClubId] = useState(null);
  const [selectTableForThird, setSelectTableForThird] = useState(false);
  const [tablePickerOpen, setTablePickerOpen] = useState(false);
  const [pendingThirdPlayer, setPendingThirdPlayer] = useState(null);
  const [thirdAssigning, setThirdAssigning] = useState(false);
  const [thirdFlowMessage, setThirdFlowMessage] = useState(null);
  const [presenceError, setPresenceError] = useState(null);
  const [thirdPickerRows, setThirdPickerRows] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [sortBy, setSortBy] = useState("classement");

  const isSheet = presentation === "sheet";
  useBodyScrollLock(open && isSheet);

  const sessionClosed =
    presenceCorrectionOnly || isSessionClosedForPresence(selectedSession);

  const thirdTableFlowActive =
    hasPermission("PLAYER_ADD_TO_SESSION") &&
    !!trainingSelectedExercise?.te_id &&
    Array.isArray(trainingTables) &&
    !sessionClosed;

  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setInviteFirstName("");
      setInviteLastName("");
      setInviteEmail("");
      setInvitePseudo("");
      setInviteError(null);
      setInviteSuccess(null);
      setInviteExpanded(false);
      setSelectTableForThird(false);
      setTablePickerOpen(false);
      setPendingThirdPlayer(null);
      setThirdFlowMessage(null);
      setPresenceError(null);
      setThirdPickerRows([]);
      setActiveFilter("all");
      setSortBy("classement");
    }
  }, [open]);

  useEffect(() => {
    if (open && demoClub) {
      setInviteExpanded(true);
    }
  }, [open, demoClub]);

  useEffect(() => {
    if (open && selectedSession?.ts_id && selectedgrp?.groupDetails?.gr_id) {
      console.log("🔍 PlayerPresenceDialog - Dialog opened, fetching data...");
      console.log("🔍 PlayerPresenceDialog - selectedgrp:", selectedgrp);
      console.log("🔍 PlayerPresenceDialog - selectedgrp.groupDetails:", selectedgrp?.groupDetails);
      console.log("🔍 PlayerPresenceDialog - gr_id:", selectedgrp?.groupDetails?.gr_id);
      fetchPlayerList();
      fetchCoaches();
    } else {
      console.log("⚠️ PlayerPresenceDialog - Dialog not ready:", { open, selectedSession: !!selectedSession });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedSession?.ts_id, selectedgrp?.groupDetails?.gr_id]);

  const fetchPlayerList = async () => {
    setLoading(true);
    try {
      const rosterGrId = resolvePresenceGroupId(selectedSession, selectedgrp);
      const params = {
        ts_id: selectedSession.ts_id,
        gr_id: rosterGrId,
      };
      const response = await grpplayerlist(params);
      const players = response.data.data || [];
      console.log("🔍 PlayerPresenceDialog - Players data:", players);
      console.log("🔍 PlayerPresenceDialog - Sample player:", players[0]);
      setAllPlayers(players);
    } catch (error) {
      console.error("❌ Error fetching player list:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoaches = async () => {
    try {
      if (!selectedgrp?.groupDetails?.gr_id) {
        console.log("⚠️ PlayerPresenceDialog - No group ID available");
        return;
      }
      console.log("🔍 PlayerPresenceDialog - Fetching coaches for group:", selectedgrp.groupDetails.gr_id);
      const response = await groupdetails(selectedgrp.groupDetails.gr_id);
      console.log("🔍 PlayerPresenceDialog - Full response:", response.data);
      const groupData = response.data.data || {};
      console.log("🔍 PlayerPresenceDialog - Group data:", groupData);
      console.log("🔍 PlayerPresenceDialog - tbl_coaches:", groupData.tbl_coaches);
      const coachesList = groupData.tbl_coaches || [];
      console.log("🔍 PlayerPresenceDialog - Coaches list:", coachesList);
      console.log("🔍 PlayerPresenceDialog - Coaches count:", coachesList.length);
      if (coachesList.length > 0) {
        console.log("🔍 PlayerPresenceDialog - First coach:", coachesList[0]);
        console.log("🔍 PlayerPresenceDialog - First coach tbl_user:", coachesList[0]?.tbl_user);
      }
      setCoaches(coachesList);
      const clubFromGroup =
        groupData.club_id ||
        groupData.groupDetails?.club_id ||
        groupData.tbl_group?.club_id ||
        null;
      if (clubFromGroup) {
        setResolvedClubId(clubFromGroup);
      }
      if (groupData.is_demo === true || groupData.club_code === "demo") {
        setGroupIsDemo(true);
      }
    } catch (error) {
      console.error("❌ Error fetching coaches:", error);
      console.error("❌ Error details:", error.response?.data || error.message);
    }
  };

  const assignThirdPlayerToTable = async (table) => {
    if (
      !pendingThirdPlayer?.player_id ||
      !table?.player1?.player_id ||
      !table?.player2?.player_id ||
      !selectedSession?.ts_id ||
      !trainingSelectedExercise?.te_id
    ) {
      return;
    }
    setThirdAssigning(true);
    setThirdFlowMessage(null);
    try {
      await playeradtable({
        table_id: table.table_id,
        ts_id: selectedSession.ts_id,
        te_id: trainingSelectedExercise.te_id,
        player1: table.player1.player_id,
        player2: table.player2.player_id,
        player3: pendingThirdPlayer.player_id,
      });
      setTablePickerOpen(false);
      setPendingThirdPlayer(null);
      setThirdPickerRows([]);
      if (onAfterThirdPlayerTableAssign && typeof onAfterThirdPlayerTableAssign === "function") {
        await onAfterThirdPlayerTableAssign();
      }
      if (onUpdate && typeof onUpdate === "function") {
        await onUpdate();
      }
      await fetchPlayerList();
    } catch (error) {
      console.error("❌ Error assigning third player to table:", error);
      setThirdFlowMessage(
        error?.response?.data?.message ||
          error?.message ||
          t("errorAssigningThirdPlayer") ||
          "Could not assign third player to this table."
      );
    } finally {
      setThirdAssigning(false);
    }
  };

  const handlePresenceChange = async (player, presence) => {
    setProcessing((prev) => ({ ...prev, [player.player_id]: true }));
    setThirdFlowMessage(null);
    setPresenceError(null);
    try {
      const params = {
        ts_id: selectedSession.ts_id,
        gr_id: resolvePresenceGroupId(selectedSession, selectedgrp),
        ts_present: presence,
        player_id: player.player_id,
      };

      await playerpresent(params);

      // Update local state immediately for instant UI feedback
      const nextPlayers = allPlayers.map((p) =>
        p.player_id === player.player_id ? { ...p, presensee: presence } : p
      );
      setAllPlayers(nextPlayers);

      // Refresh parent in the background — never treat refresh failure as a
      // presence failure (that used to silently revert a successful mark).
      if (onUpdate && typeof onUpdate === "function") {
        try {
          await onUpdate();
        } catch (refreshErr) {
          console.error(
            "PlayerPresenceDialog - onUpdate after presence failed:",
            refreshErr
          );
        }
      }

      if (
        presence === true &&
        !sessionClosed &&
        onPlayerMarkedPresent &&
        typeof onPlayerMarkedPresent === "function"
      ) {
        try {
          const presentPlayers = nextPlayers.filter((p) => p.presensee === true);
          const seatResult = await onPlayerMarkedPresent({
            player,
            presentPlayers,
          });
          if (seatResult && seatResult.ok === false && seatResult.message) {
            setPresenceError(seatResult.message);
          }
        } catch (seatErr) {
          console.error(
            "PlayerPresenceDialog - onPlayerMarkedPresent failed:",
            seatErr
          );
          setPresenceError(
            seatErr?.message ||
              t("presenceSeatAssignFailed") ||
              "Could not seat newly present players."
          );
        }
      }

      if (
        presence === true &&
        selectTableForThird &&
        thirdTableFlowActive &&
        selectedgrp?.groupDetails?.gr_id &&
        trainingSelectedExercise?.te_id
      ) {
        try {
          let raw = Array.isArray(trainingTables) ? trainingTables : [];
          if (!raw.length) {
            const tlParams = {
              gr_id: selectedgrp.groupDetails.gr_id,
              ts_id: selectedSession.ts_id,
              te_id: trainingSelectedExercise.te_id,
            };
            const tlRes = await tablelist(tlParams);
            raw = Array.isArray(tlRes.data?.data) ? tlRes.data.data : [];
          }
          const stationScoped = filterTablesForStation(raw, tsgId, stations);
          const eligible = [...stationScoped]
            .sort((a, b) => (a.table_number || 0) - (b.table_number || 0))
            .filter(tableEligibleForThirdPlayer);

          if (eligible.length === 0) {
            setThirdFlowMessage(
              t("noTableReadyForThirdPlayer") ||
                "No table has two players with an empty third seat. Add two players to a table first."
            );
          } else {
            setThirdPickerRows(eligible);
            setPendingThirdPlayer(player);
            setTablePickerOpen(true);
          }
        } catch (tlErr) {
          console.error("❌ Error loading tables for third player:", tlErr);
          setThirdFlowMessage(
            t("errorLoadingTables") || "Could not load tables. Try again."
          );
        }
      }
    } catch (error) {
      console.error("❌ Error updating presence:", error);
      if (error?.code === "NEWCOMER_PAYMENT_REQUIRED") {
        setPaymentGate({ ...(error.data || {}), player });
      }
      setPresenceError(
        error?.message ||
          error?.code ||
          t("presenceUpdateFailed") ||
          "Could not update presence. Try again."
      );
      fetchPlayerList();
    } finally {
      setProcessing((prev) => ({ ...prev, [player.player_id]: false }));
    }
  };

  const getPlayerName = (player) =>
    playerPresenceDisplayName(
      player,
      `${t("player")} #${player.player_id?.slice(-4) || ""}`
    );

  const getPlayerInitials = (player) => playerPresenceInitials(player);

  const getPresenceStatus = (player) => {
    if (player.presensee === true) return "present";
    if (player.presensee === false) return "absent";
    return "unmarked";
  };

  // Handle dialog close — refresh parent (players + tables) before unmounting the dialog
  const handleClose = async () => {
    console.log("🔄 PlayerPresenceDialog - Dialog closing, refreshing player list...");
    const presentPlayers = allPlayers.filter((p) => p.presensee === true);
    try {
      if (onUpdate && typeof onUpdate === "function") {
        try {
          await onUpdate();
        } catch (refreshErr) {
          console.error("PlayerPresenceDialog - onUpdate failed:", refreshErr);
        }
      }
      if (!sessionClosed && onDone && typeof onDone === "function") {
        const seatResult = await onDone({ presentPlayers });
        if (seatResult && seatResult.ok === false && seatResult.message) {
          // Sheet is closing — still log; parent tableSetup.error retains detail.
          console.warn(
            "PlayerPresenceDialog - seating on Done:",
            seatResult.message
          );
        }
      }
    } catch (e) {
      console.error("PlayerPresenceDialog - close handlers failed:", e);
    } finally {
      setTablePickerOpen(false);
      setPendingThirdPlayer(null);
      setThirdPickerRows([]);
      setThirdFlowMessage(null);
      setPresenceError(null);
      onClose();
    }
  };

  const qTrim = searchQuery.trim();
  const qLower = qTrim.toLowerCase();

  const summaryPresentCount = useMemo(
    () => allPlayers.filter((p) => p.presensee === true).length,
    [allPlayers]
  );
  const summaryAbsentCount = useMemo(
    () => allPlayers.filter((p) => p.presensee === false).length,
    [allPlayers]
  );
  const summaryUnmarkedCount = useMemo(
    () => allPlayers.filter((p) => p.presensee === null || p.presensee === undefined).length,
    [allPlayers]
  );

  const presentPlayers = useMemo(
    () =>
      allPlayers
        .filter((p) => p.presensee === true && playerMatchesQuery(p, qLower))
        .slice()
        .sort((a, b) => comparePlayersBySort(a, b, sortBy)),
    [allPlayers, qLower, sortBy]
  );
  const absentPlayers = useMemo(
    () =>
      allPlayers
        .filter((p) => p.presensee === false && playerMatchesQuery(p, qLower))
        .slice()
        .sort((a, b) => comparePlayersBySort(a, b, sortBy)),
    [allPlayers, qLower, sortBy]
  );
  const unmarkedPlayers = useMemo(
    () =>
      allPlayers
        .filter(
          (p) =>
            (p.presensee === null || p.presensee === undefined) &&
            playerMatchesQuery(p, qLower)
        )
        .slice()
        .sort((a, b) => comparePlayersBySort(a, b, sortBy)),
    [allPlayers, qLower, sortBy]
  );

  const filteredCoaches = useMemo(
    () => coaches.filter((c) => coachMatchesQuery(c, qLower)),
    [coaches, qLower]
  );

  const clubId =
    selectedgrp?.groupDetails?.club_id || resolvedClubId || null;
  const canInvitePlayer =
    (allowInviteWhenClosed || !sessionClosed) &&
    (hasPermission("PLAYER_ADD_TO_SESSION") || hasPermission("PLAYER_INVITE")) &&
    !!selectedgrp?.groupDetails?.gr_id &&
    (demoClub || !!clubId);

  const noPlayerMatchesSearch =
    qTrim.length >= 2 && !allPlayers.some((p) => playerMatchesQuery(p, qLower));

  const showInvitePanel =
    canInvitePlayer &&
    !loading &&
    !demoClub &&
    (allPlayers.length === 0 || noPlayerMatchesSearch || inviteExpanded);

  const applySearchToInviteNames = () => {
    const parts = qTrim.split(/\s+/).filter(Boolean);
    if (demoClub) {
      setInvitePseudo(qTrim);
      return;
    }
    setInviteFirstName(parts[0] || "");
    setInviteLastName(parts.slice(1).join(" ") || "");
  };

  const handleInviteSubmit = async (e) => {
    e?.preventDefault?.();
    setInviteError(null);
    setInviteSuccess(null);
    const fn = inviteFirstName.trim();
    const ln = inviteLastName.trim();
    const em = inviteEmail.trim().toLowerCase();
    const ps = invitePseudo.trim();

    if (demoClub) {
      if (!ps) {
        setInviteError(t("pseudoRequired") || "A player pseudo is required");
        return;
      }
      if (em && !EMAIL_RE.test(em)) {
        setInviteError(
          t("please_enter_valid_email") || "Please enter a valid email address"
        );
        return;
      }
    } else {
      if (!fn || !ln) {
        setInviteError(t("firstLastNameRequired") || "First and last name are required");
        return;
      }
      if (em && !EMAIL_RE.test(em)) {
        setInviteError(t("please_enter_valid_email") || "Please enter a valid email address");
        return;
      }
    }

    setInviteSubmitting(true);
    try {
      let result;
      let rosterOnly = false;
      if (demoClub) {
        result = await addDemoSessionPlayer({
          pseudo: ps,
          first_name: fn,
          last_name: ln,
          email: em || undefined,
          club_id: clubId,
          gr_id: selectedgrp.groupDetails.gr_id,
          ts_id: selectedSession?.ts_id,
        });
      } else if (em) {
        result = await inviteTrainingSessionGuest({
          first_name: fn,
          last_name: ln,
          email: em,
          club_id: clubId,
          gr_id: selectedgrp.groupDetails.gr_id,
          ts_id: selectedSession?.ts_id,
        });
      } else {
        rosterOnly = true;
        result = await createClubPlayerInvite({
          first_name: fn,
          last_name: ln,
          club_id: clubId,
          gr_id: selectedgrp.groupDetails.gr_id,
          is_active: false,
        });
      }
      setInviteFirstName("");
      setInviteLastName("");
      setInviteEmail("");
      setInvitePseudo("");
      setSearchQuery("");
      setInviteExpanded(true);
      const payload = result?.data || result || {};
      const createdPlayerId = payload.player_id || null;

      if (rosterOnly && createdPlayerId && selectedSession?.ts_id) {
        try {
          await playerpresent({
            ts_id: selectedSession.ts_id,
            gr_id: resolvePresenceGroupId(selectedSession, selectedgrp),
            ts_present: true,
            player_id: createdPlayerId,
          });
        } catch (presenceErr) {
          console.warn("PlayerPresenceDialog - mark present after roster add:", presenceErr);
        }
      }

      setInviteSuccess(
        demoClub
          ? t("demoAddPlayerSuccess") ||
              "Player added and marked present. Video and heart rate are kept for one month."
          : rosterOnly
            ? t("rosterPlayerAddedSuccess") ||
                "Player added to the roster and marked present. They do not need the app — you can manage presence anytime."
            : payload.already_member || result?.already_member
              ? t("inviteGuestAddedExisting") ||
                  "Player added to this session. You can manage their presence now."
              : result?.message ||
                  t("inviteGuestSuccess") ||
                  "Invitation sent. They can join live training after confirming their email."
      );
      await fetchPlayerList();
      if (onUpdate && typeof onUpdate === "function") {
        onUpdate();
      }
      if (
        createdPlayerId &&
        onPlayerMarkedPresent &&
        typeof onPlayerMarkedPresent === "function" &&
        (demoClub || rosterOnly)
      ) {
        try {
          await onPlayerMarkedPresent({
            player_id: createdPlayerId,
            user_id: payload.user_id,
            user_first_name: payload.user_first_name || payload.first_name || fn,
            user_last_name: payload.user_last_name || payload.last_name || ln,
            user_alias: payload.pseudo || null,
          });
        } catch (seatErr) {
          console.warn("PlayerPresenceDialog - auto-seat after add:", seatErr);
        }
      }
    } catch (err) {
      setInviteError(
        err?.message || t("invitePlayerError") || "Could not add player. Try again."
      );
    } finally {
      setInviteSubmitting(false);
    }
  };

  const totalFilteredPlayers =
    presentPlayers.length + absentPlayers.length + unmarkedPlayers.length;

  const toggleFilter = (filter) => {
    setActiveFilter((prev) => (prev === filter ? "all" : filter));
  };

  const renderSheetPlayerRows = (players, status) =>
    players.map((player) => (
      <CompactPlayerRow
        key={player.player_id}
        player={player}
        status={status}
        onAdd={() =>
          hasPermission("PLAYER_ADD_TO_SESSION") &&
          handlePresenceChange(player, true)
        }
        onRemove={() =>
          hasPermission("PLAYER_REMOVE_FROM_SESSION") &&
          handlePresenceChange(player, false)
        }
        processing={processing[player.player_id]}
        getPlayerName={getPlayerName}
        getPlayerInitials={getPlayerInitials}
        hasPermission={hasPermission}
        t={t}
      />
    ));

  const renderSheetPlayerSections = () => {
    const showUnmarked = activeFilter === "all" || activeFilter === "unmarked";
    const showPresent = activeFilter === "all" || activeFilter === "present";
    const showAbsent = activeFilter === "all" || activeFilter === "absent";

    if (
      (showUnmarked && unmarkedPlayers.length > 0) ||
      (showPresent && presentPlayers.length > 0) ||
      (showAbsent && absentPlayers.length > 0)
    ) {
      return (
        <>
          {showUnmarked && unmarkedPlayers.length > 0 ? (
            <>
              {activeFilter === "all" ? (
                <p className="lt-pp-sheet__section-label">
                  {t("unmarkedPlayers") || "Unmarked"} ({unmarkedPlayers.length})
                </p>
              ) : null}
              <div className="lt-pp-sheet__list">
                {renderSheetPlayerRows(unmarkedPlayers, "unmarked")}
              </div>
            </>
          ) : null}
          {showPresent && presentPlayers.length > 0 ? (
            <>
              {activeFilter === "all" ? (
                <p className="lt-pp-sheet__section-label">
                  {t("presentPlayers") || "Present"} ({presentPlayers.length})
                </p>
              ) : null}
              <div className="lt-pp-sheet__list">
                {renderSheetPlayerRows(presentPlayers, "present")}
              </div>
            </>
          ) : null}
          {showAbsent && absentPlayers.length > 0 ? (
            <>
              {activeFilter === "all" ? (
                <p className="lt-pp-sheet__section-label">
                  {t("absentPlayers") || "Absent"} ({absentPlayers.length})
                </p>
              ) : null}
              <div className="lt-pp-sheet__list">
                {renderSheetPlayerRows(absentPlayers, "absent")}
              </div>
            </>
          ) : null}
        </>
      );
    }

    if (!loading && allPlayers.length > 0 && qTrim.length >= 2 && !showInvitePanel) {
      return (
        <p className="lt-pp-sheet__empty">
          {t("noPlayersMatchSearch") || "No players match this search."}
        </p>
      );
    }

    if (!loading && allPlayers.length === 0) {
      return (
        <p className="lt-pp-sheet__empty">
          {t("noPlayersInGroup") || "No players in this group"}
        </p>
      );
    }

    return null;
  };

  const sheetPortalStyle =
    zIndex != null ? { zIndex } : undefined;

  const nestedSheetPortalStyle =
    zIndex != null ? { zIndex: zIndex + 10 } : undefined;

  const landscapeActive =
    isLandscape ||
    (typeof document !== "undefined" &&
      document.querySelector(".lt-page--landscape") != null);

  const tablePickerSheet =
    isSheet && tablePickerOpen ? (
      <div
        className={`lt-pp-sheet-portal lt-pp-sheet-portal--nested ${sheetThemeClass}${
          landscapeActive ? " lt-pp-sheet-portal--landscape" : ""
        }`}
        style={nestedSheetPortalStyle}
        role="presentation"
        onClick={() => {
          if (thirdAssigning) return;
          setTablePickerOpen(false);
          setPendingThirdPlayer(null);
          setThirdFlowMessage(null);
          setThirdPickerRows([]);
        }}
      >
        <div
          className={`lt-pp-sheet lt-pp-sheet--nested ${sheetThemeClass}`}
          role="dialog"
          aria-modal="true"
          aria-label={t("tablesConfigurationSubtitle") || "Tables configuration"}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="lt-pp-sheet__handle" aria-hidden="true" />
          <header className="lt-pp-sheet__header">
            <div className="lt-pp-sheet__title-wrap">
              <h2 className="lt-pp-sheet__title">
                {t("tablesConfigurationSubtitle") || "Tables configuration"}
              </h2>
              {pendingThirdPlayer ? (
                <p className="lt-pp-sheet__subtitle">
                  {(t("assignThirdPlayerLabel") || "Add as 3rd player") + ": "}
                  <strong>{getPlayerName(pendingThirdPlayer)}</strong>
                </p>
              ) : null}
            </div>
            <button
              type="button"
              className="lt-pp-sheet__close"
              onClick={() => {
                if (thirdAssigning) return;
                setTablePickerOpen(false);
                setPendingThirdPlayer(null);
                setThirdFlowMessage(null);
                setThirdPickerRows([]);
              }}
              aria-label={t("close") || "Close"}
            >
              <CloseIcon fontSize="small" />
            </button>
          </header>
          <div className="lt-pp-sheet__body">
            {thirdFlowMessage ? (
              <div className="lt-pp-sheet__alert" role="alert">
                {thirdFlowMessage}
              </div>
            ) : null}
            {thirdPickerRows.length === 0 ? (
              <p className="lt-pp-sheet__empty">
                {t("noTableReadyForThirdPlayer") ||
                  "No table has two players with a free third seat."}
              </p>
            ) : (
              <div className="lt-pp-table-list">
                {thirdPickerRows.map((tbl) => (
                  <button
                    key={tbl.table_id || tbl.table_number}
                    type="button"
                    className="lt-pp-table-item"
                    disabled={thirdAssigning}
                    onClick={() => assignThirdPlayerToTable(tbl)}
                  >
                    {formatTableSummaryLine(tbl)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    ) : null;

  if (isSheet) {
    if (!open) return null;

    const sheetTitle = t("managePlayerPresence") || "Manage Player Presence";
    const sheetSubtitle =
      sessionClosed && !allowInviteWhenClosed
        ? t("presenceCorrectionSubtitle") ||
          "Correct who attended this session"
        : t("managePlayerPresenceHint") ||
          "Add or remove players from this session";
    const sheetKicker = selectedgrp?.groupDetails?.gr_name || sheetSubtitle;

    const sheetNode = (
      <>
        <div
          className={`lt-pp-sheet-portal ${sheetThemeClass}${
            landscapeActive ? " lt-pp-sheet-portal--landscape" : ""
          }`}
          style={sheetPortalStyle}
          role="presentation"
          onClick={handleClose}
        >
          <div
            className={`lt-pp-sheet ${sheetThemeClass}`}
            role="dialog"
            aria-modal="true"
            aria-label={sheetTitle}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="lt-pp-sheet__handle" aria-hidden="true" />
            <header className="lt-pp-bar">
              <div className="lt-pp-bar__text">
                <p className="lt-pp-bar__kicker">{sheetKicker}</p>
                <h2 className="lt-pp-bar__title">{sheetTitle}</h2>
              </div>
              {canInvitePlayer && !demoClub && !showInvitePanel ? (
                <button
                  type="button"
                  className="lt-pp-bar__icon-btn"
                  onClick={() => {
                    setInviteExpanded(true);
                    applySearchToInviteNames();
                  }}
                  aria-label={t("invitePlayerAdd") || "Add player"}
                  title={t("invitePlayerAdd") || "Add player"}
                >
                  <PersonAddIcon fontSize="small" />
                </button>
              ) : null}
              <button type="button" className="lt-pp-bar__done" onClick={handleClose}>
                {t("done") || "Done"}
              </button>
            </header>

            <div className="lt-pp-sheet__toolbar">
              <div
                className="lt-pp-tally"
                role="group"
                aria-label={t("presenceSummary") || "Presence summary"}
              >
                {[
                  { id: "present", count: summaryPresentCount, label: t("present") || "Present" },
                  { id: "unmarked", count: summaryUnmarkedCount, label: t("unmarked") || "Unmarked" },
                  { id: "absent", count: summaryAbsentCount, label: t("absent") || "Absent" },
                ].map(({ id, count, label }) => (
                  <button
                    key={id}
                    type="button"
                    className={`lt-pp-tally__cell lt-pp-tally__cell--${id}${
                      activeFilter === id ? " lt-pp-tally__cell--active" : ""
                    }`}
                    onClick={() => toggleFilter(id)}
                    aria-pressed={activeFilter === id}
                  >
                    <span className="lt-pp-tally__value">{count}</span>
                    <span className="lt-pp-tally__label">{label}</span>
                  </button>
                ))}
              </div>

              <div className="lt-pp-sheet__search-row">
                <label className="lt-pp-sheet__search">
                  <SearchIcon className="lt-pp-sheet__search-icon" fontSize="small" />
                  <input
                    className="lt-pp-sheet__search-input"
                    type="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    disabled={loading}
                    placeholder={t("searchPlayersPlaceholder") || "Search players by name…"}
                    aria-label={t("searchPlayersPlaceholder") || "Search players"}
                  />
                </label>
                <label className="lt-pp-sheet__sort">
                  <SortIcon className="lt-pp-sheet__sort-icon" fontSize="small" />
                  <select
                    className="lt-pp-sheet__sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    aria-label={t("sortPlayers") || "Sort players"}
                  >
                    <option value="classement">{t("classement") || "Classement"}</option>
                    <option value="lastName">{t("lastName") || "Last name"}</option>
                    <option value="firstName">{t("firstName") || "First name"}</option>
                  </select>
                </label>
              </div>

              {thirdTableFlowActive ? (
                <label className="lt-pp-sheet__option">
                  <input
                    type="checkbox"
                    checked={selectTableForThird}
                    onChange={(e) => setSelectTableForThird(e.target.checked)}
                    disabled={loading}
                  />
                  <span>{t("selectTableForThirdPlayer") || "Assign to table when adding"}</span>
                </label>
              ) : null}
            </div>

            {presenceError ? (
              <div className="lt-pp-sheet__alert lt-pp-sheet__alert--error" role="alert">
                {presenceError}
              </div>
            ) : null}

            {thirdFlowMessage && !tablePickerOpen ? (
              <div className="lt-pp-sheet__alert" role="alert">
                {thirdFlowMessage}
              </div>
            ) : null}

            <div className="lt-pp-sheet__body">
              {loading ? (
                <div className="lt-pp-sheet__loading">
                  <CircularProgress size={32} />
                </div>
              ) : (
                <>
                  {canInvitePlayer && demoClub ? (
                    <DemoGuestInviteCollapsible defaultExpanded={false} qrPresentation="fullscreen">
                      <form className="lt-pp-sheet__invite lt-pp-sheet__invite--nested" onSubmit={handleInviteSubmit}>
                        <p className="lt-pp-sheet__invite-title">
                          {t("demoAddPlayer") || "Add player to record"}
                        </p>
                        <p className="lt-pp-sheet__invite-hint">
                          {t("demoAddPlayerHint") ||
                            "Type any pseudo to record video and heart rate. Email is optional and does not make them a club member. Demo recordings are kept for one month."}
                        </p>
                        {inviteError ? (
                          <p className="lt-pp-sheet__invite-error" role="alert">
                            {inviteError}
                          </p>
                        ) : null}
                        {inviteSuccess ? (
                          <p className="lt-pp-sheet__invite-success" role="status">
                            {inviteSuccess}
                          </p>
                        ) : null}
                        <div className="lt-pp-sheet__invite-fields">
                          <input
                            className="lt-pp-sheet__invite-input"
                            placeholder={t("playerPseudo") || "Pseudo"}
                            value={invitePseudo}
                            onChange={(e) => setInvitePseudo(e.target.value)}
                            disabled={inviteSubmitting}
                            autoComplete="nickname"
                            aria-label={t("playerPseudo") || "Pseudo"}
                          />
                          <input
                            className="lt-pp-sheet__invite-input lt-pp-sheet__invite-input--email"
                            type="email"
                            placeholder={t("emailOptional") || "Email (optional)"}
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            disabled={inviteSubmitting}
                            autoComplete="email"
                            inputMode="email"
                            aria-label={t("emailOptional") || "Email (optional)"}
                          />
                        </div>
                        {qTrim.length >= 2 ? (
                          <button
                            type="button"
                            className="lt-pp-sheet__filter"
                            style={{ marginBottom: 8 }}
                            onClick={applySearchToInviteNames}
                          >
                            {t("useSearchAsName") || "Use search text as name"}
                          </button>
                        ) : null}
                        <button
                          type="submit"
                          className="lt-pp-sheet__invite-btn"
                          disabled={inviteSubmitting}
                        >
                          {inviteSubmitting ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <PersonAddIcon fontSize="small" />
                          )}
                          {inviteSubmitting
                            ? t("saving") || "Saving…"
                            : t("demoAddPlayerBtn") || "Add and record"}
                        </button>
                      </form>
                    </DemoGuestInviteCollapsible>
                  ) : null}

                  {canInvitePlayer && !demoClub && showInvitePanel ? (
                    <form className="lt-pp-sheet__invite" onSubmit={handleInviteSubmit}>
                      <p className="lt-pp-sheet__invite-title">
                        {t("addRosterPlayer") || "Add player to roster"}
                      </p>
                      <p className="lt-pp-sheet__invite-hint">
                        {t("addRosterPlayerHint") ||
                          "First and last name are enough. Email is optional — leave it empty for members who will not use the app. You can mark presence now; add email later if they should register."}
                      </p>
                      {inviteError ? (
                        <p className="lt-pp-sheet__invite-error" role="alert">
                          {inviteError}
                        </p>
                      ) : null}
                      {inviteSuccess ? (
                        <p className="lt-pp-sheet__invite-success" role="status">
                          {inviteSuccess}
                        </p>
                      ) : null}
                      <div className="lt-pp-sheet__invite-fields">
                        <input
                          className="lt-pp-sheet__invite-input"
                          placeholder={t("firstName") || "First name"}
                          value={inviteFirstName}
                          onChange={(e) => setInviteFirstName(e.target.value)}
                          disabled={inviteSubmitting}
                          autoComplete="given-name"
                          aria-label={t("firstName") || "First name"}
                        />
                        <input
                          className="lt-pp-sheet__invite-input"
                          placeholder={t("lastName") || "Last name"}
                          value={inviteLastName}
                          onChange={(e) => setInviteLastName(e.target.value)}
                          disabled={inviteSubmitting}
                          autoComplete="family-name"
                          aria-label={t("lastName") || "Last name"}
                        />
                        <input
                          className="lt-pp-sheet__invite-input lt-pp-sheet__invite-input--email"
                          type="email"
                          placeholder={t("emailOptional") || "Email (optional)"}
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          disabled={inviteSubmitting}
                          autoComplete="email"
                          inputMode="email"
                          aria-label={t("emailOptional") || "Email (optional)"}
                        />
                      </div>
                      {qTrim.length >= 2 ? (
                        <button
                          type="button"
                          className="lt-pp-sheet__filter"
                          style={{ marginBottom: 8 }}
                          onClick={applySearchToInviteNames}
                        >
                          {t("useSearchAsName") || "Use search text as name"}
                        </button>
                      ) : null}
                      <button
                        type="submit"
                        className="lt-pp-sheet__invite-btn"
                        disabled={inviteSubmitting}
                      >
                        {inviteSubmitting ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <PersonAddIcon fontSize="small" />
                        )}
                        {inviteSubmitting
                          ? t("saving") || "Saving…"
                          : inviteEmail.trim()
                            ? t("invitePlayerAdd") || "Invite guest"
                            : t("addRosterPlayerBtn") || "Add and mark present"}
                      </button>
                    </form>
                  ) : null}

                  {filteredCoaches.length > 0 && activeFilter === "all" ? (
                    <>
                      <p className="lt-pp-sheet__section-label">
                        {t("coaches") || "Coaches"} ({filteredCoaches.length})
                      </p>
                      <div className="lt-pp-sheet__coaches">
                        {filteredCoaches.map((coach, index) => {
                          const coachUser = coach.tbl_user || coach.user || {};
                          const coachId = coach.coach_id || coach.id || "";
                          const coachName = playerPresenceDisplayName(
                            coachUser,
                            `${t("coach")} #${coachId?.slice(-4) || ""}`
                          );
                          const coachInitials = playerPresenceInitials(coachUser, "C");
                          return (
                            <span
                              key={coachId || `coach-${index}`}
                              className="lt-pp-sheet__coach-chip"
                            >
                              <span className="lt-pp-sheet__coach-avatar">
                                {coachUser.user_image ? (
                                  <img src={coachUser.user_image} alt="" />
                                ) : (
                                  coachInitials
                                )}
                              </span>
                              {coachName}
                            </span>
                          );
                        })}
                      </div>
                    </>
                  ) : null}

                  {renderSheetPlayerSections()}
                </>
              )}
            </div>
          </div>
        </div>
        {tablePickerSheet}
      </>
    );

    const mount =
      portalRoot ||
      (typeof document !== "undefined"
        ? getLiveTrainingPortalRoot() || document.body
        : null);
    if (!mount) return null;
    return createPortal(sheetNode, mount);
  }

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={zIndex != null ? { zIndex } : undefined}
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
          bgcolor: "background.paper",
          color: "text.primary",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6" component="div">
          {t("managePlayerPresence") || "Manage Player Presence"}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ width: "100%", maxWidth: "100%" }}>
        <TextField
          fullWidth
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          disabled={loading}
          placeholder={t("searchPlayersPlaceholder") || "Search players by name…"}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
        {sessionClosed ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("presenceCorrectionHint") ||
              "This session is finalized. You can still correct who was present or absent."}
          </Alert>
        ) : null}
        {thirdTableFlowActive && (
          <FormControlLabel
            sx={{ display: "flex", alignItems: "flex-start", ml: 0, mb: 2 }}
            control={
              <Checkbox
                checked={selectTableForThird}
                onChange={(e) => setSelectTableForThird(e.target.checked)}
                disabled={loading}
                size="small"
              />
            }
            label={
              <Typography variant="body2" component="span">
                {t("selectTableForThirdPlayer") || "Select table"}
              </Typography>
            }
          />
        )}
        {thirdFlowMessage && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setThirdFlowMessage(null)}>
            {thirdFlowMessage}
          </Alert>
        )}
        {presenceError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPresenceError(null)}>
            {presenceError}
          </Alert>
        )}
        <AddNewcomerDialog
          open={newcomerOpen}
          tsId={selectedSession?.ts_id}
          onClose={() => setNewcomerOpen(false)}
          onCreated={() => {
            fetchPlayerList();
          }}
        />
        <Dialog open={Boolean(paymentGate)} onClose={() => setPaymentGate(null)} fullWidth maxWidth="sm">
          <DialogTitle>Free days are used</DialogTitle>
          <DialogContent>
            <p>Transfer the fee with this reference, or pay by card if the club has Stripe.</p>
            <p>Account holder: {paymentGate?.bank?.account_holder || "Not set"}</p>
            <p>IBAN: {paymentGate?.bank?.iban || "—"}</p>
            <p>BIC: {paymentGate?.bank?.bic || "—"}</p>
            <p>Membership reference: {paymentGate?.references?.membership}</p>
            <p>This session reference: {paymentGate?.references?.session}</p>
          </DialogContent>
          <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
            <Button
              onClick={async () => {
                await recordNewcomerPayment(paymentGate.dossier_id, {
                  kind: "session",
                  method: "transfer",
                  session_date: paymentGate.session_date,
                  mark_received: true,
                });
                const player = paymentGate.player;
                setPaymentGate(null);
                if (player) handlePresenceChange(player, true);
              }}
            >
              Mark this session received
            </Button>
            <Button
              onClick={async () => {
                await recordNewcomerPayment(paymentGate.dossier_id, {
                  kind: "membership",
                  method: "transfer",
                  mark_received: true,
                });
                const player = paymentGate.player;
                setPaymentGate(null);
                if (player) handlePresenceChange(player, true);
              }}
            >
              Mark membership received
            </Button>
            {paymentGate?.stripe_ready ? (
              <Button
                variant="contained"
                onClick={async () => {
                  const payload = await startNewcomerCardPayment({
                    dossierId: paymentGate.dossier_id,
                    kind: "session",
                    session_date: paymentGate.session_date,
                  });
                  if (payload.data?.url) window.location.assign(payload.data.url);
                }}
              >
                Pay this session by card
              </Button>
            ) : null}
            <Button onClick={() => setPaymentGate(null)}>Close</Button>
          </DialogActions>
        </Dialog>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 1, width: "100%" }}>
            {canInvitePlayer && demoClub ? (
              <DemoGuestInviteCollapsible defaultExpanded={false} className="lt-pp-desktop-invite">
                <Box
                  component="form"
                  onSubmit={handleInviteSubmit}
                  sx={{ mt: 1.5 }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {t("demoAddPlayer") || "Add player to record"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t("demoAddPlayerHint") ||
                      "Type any pseudo to record video and heart rate. Email is optional and does not make them a club member. Demo recordings are kept for one month."}
                  </Typography>
                  {qTrim.length >= 2 && (
                    <Button
                      type="button"
                      size="small"
                      onClick={applySearchToInviteNames}
                      sx={{ mb: 1.5, textTransform: "none" }}
                    >
                      {t("useSearchAsName") || "Use search text as name"}
                    </Button>
                  )}
                  {inviteError && (
                    <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setInviteError(null)}>
                      {inviteError}
                    </Alert>
                  )}
                  {inviteSuccess && (
                    <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setInviteSuccess(null)}>
                      {inviteSuccess}
                    </Alert>
                  )}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
                    <TextField
                      label={t("playerPseudo") || "Pseudo"}
                      value={invitePseudo}
                      onChange={(e) => setInvitePseudo(e.target.value)}
                      size="small"
                      sx={{ flex: "1 1 160px" }}
                      disabled={inviteSubmitting}
                      autoComplete="nickname"
                    />
                    <TextField
                      type="email"
                      label={t("emailOptional") || "Email (optional)"}
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      size="small"
                      sx={{ flex: "1 1 220px" }}
                      disabled={inviteSubmitting}
                      autoComplete="email"
                    />
                  </Box>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={
                      inviteSubmitting ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <PersonAddIcon />
                      )
                    }
                    disabled={inviteSubmitting}
                    sx={{ textTransform: "none" }}
                  >
                    {inviteSubmitting
                      ? t("saving") || "Saving…"
                      : t("demoAddPlayerBtn") || "Add and record"}
                  </Button>
                </Box>
              </DemoGuestInviteCollapsible>
            ) : null}
            {canInvitePlayer && !demoClub && !showInvitePanel && (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={() => {
                    setInviteExpanded(true);
                    applySearchToInviteNames();
                  }}
                  sx={{ textTransform: "none" }}
                >
                  {t("invitePlayerAdd") || "Add player"}
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  onClick={() => setNewcomerOpen(true)}
                  sx={{ textTransform: "none" }}
                >
                  Add newcomer
                </Button>
              </Box>
            )}
            {canInvitePlayer && !demoClub && showInvitePanel && (
              <Box
                component="form"
                onSubmit={handleInviteSubmit}
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "primary.main",
                  bgcolor: "action.hover",
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  {t("addRosterPlayer") || "Add player to roster"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t("addRosterPlayerHint") ||
                    "First and last name are enough. Email is optional — leave it empty for members who will not use the app. You can mark presence now; add email later if they should register."}
                </Typography>
                {qTrim.length >= 2 && (
                  <Button
                    type="button"
                    size="small"
                    onClick={applySearchToInviteNames}
                    sx={{ mb: 1.5, textTransform: "none" }}
                  >
                    {t("useSearchAsName") || "Use search text as name"}
                  </Button>
                )}
                {inviteError && (
                  <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setInviteError(null)}>
                    {inviteError}
                  </Alert>
                )}
                {inviteSuccess && (
                  <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setInviteSuccess(null)}>
                    {inviteSuccess}
                  </Alert>
                )}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
                  <TextField
                    label={t("firstName") || "First name"}
                    value={inviteFirstName}
                    onChange={(e) => setInviteFirstName(e.target.value)}
                    size="small"
                    sx={{ flex: "1 1 160px" }}
                    disabled={inviteSubmitting}
                    autoComplete="given-name"
                  />
                  <TextField
                    label={t("lastName") || "Last name"}
                    value={inviteLastName}
                    onChange={(e) => setInviteLastName(e.target.value)}
                    size="small"
                    sx={{ flex: "1 1 160px" }}
                    disabled={inviteSubmitting}
                    autoComplete="family-name"
                  />
                  <TextField
                    type="email"
                    label={t("emailOptional") || "Email (optional)"}
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    size="small"
                    sx={{ flex: "1 1 220px" }}
                    disabled={inviteSubmitting}
                    autoComplete="email"
                  />
                </Box>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={inviteSubmitting}
                  startIcon={inviteSubmitting ? <CircularProgress size={18} color="inherit" /> : <PersonAddIcon />}
                >
                  {inviteSubmitting
                    ? t("saving") || "Saving…"
                    : inviteEmail.trim()
                      ? t("invitePlayerAdd") || "Invite guest"
                      : t("addRosterPlayerBtn") || "Add and mark present"}
                </Button>
              </Box>
            )}

            {/* Summary Stats (full roster — not affected by search) */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={4}>
                <Box sx={presenceStatBoxSx(theme, "success")}>
                  <Typography variant="h4" color="success.main">
                    {summaryPresentCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("present") || "Present"}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={presenceStatBoxSx(theme, "warning")}>
                  <Typography variant="h4" color="warning.main">
                    {summaryUnmarkedCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("unmarked") || "Unmarked"}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={presenceStatBoxSx(theme, "error")}>
                  <Typography variant="h4" color="error.main">
                    {summaryAbsentCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("absent") || "Absent"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                mb: 2.5,
              }}
              role="group"
              aria-label={t("sortPlayers") || "Sort players"}
            >
              {[
                { id: "classement", label: t("classement") || "Classement" },
                { id: "lastName", label: t("lastName") || "Last name" },
                { id: "firstName", label: t("firstName") || "First name" },
              ].map(({ id, label }) => (
                <Chip
                  key={id}
                  label={label}
                  size="small"
                  clickable
                  color={sortBy === id ? "primary" : "default"}
                  variant={sortBy === id ? "filled" : "outlined"}
                  onClick={() => setSortBy(id)}
                />
              ))}
            </Box>

            {/* Coaches Section */}
            {coaches && coaches.length > 0 ? (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  {t("coaches") || "Coaches"} ({filteredCoaches.length}
                  {qTrim ? ` / ${coaches.length}` : ""})
                </Typography>
                {filteredCoaches.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t("noCoachesMatchSearch") || "No coaches match this search."}
                  </Typography>
                ) : (
                <Grid container spacing={2} sx={{ width: "100%", alignItems: "stretch", display: "flex", flexWrap: "wrap" }}>
                  {filteredCoaches.map((coach, index) => {
                    // Handle both direct coach object and nested structure
                    const coachUser = coach.tbl_user || coach.user || {};
                    const coachId = coach.coach_id || coach.id || "";
                    const coachName = playerPresenceDisplayName(
                      coachUser,
                      `${t("coach")} #${coachId?.slice(-4) || ""}`
                    );
                    const coachInitials = playerPresenceInitials(coachUser, "C");
                    
                    return (
                      <Grid 
                        item 
                        xs={12} 
                        sm={6}
                        key={coachId || `coach-${index}`}
                        sx={{
                          display: "flex",
                          minHeight: 90,
                          flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 16px)", md: "1 1 calc(50% - 16px)" },
                          maxWidth: { xs: "100%", sm: "calc(50% - 16px)", md: "calc(50% - 16px)" },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            p: 2,
                            border: "2px solid",
                            borderColor: "secondary.main",
                            borderRadius: 2,
                            bgcolor: "background.paper",
                            color: "text.primary",
                            minHeight: 90,
                            height: "100%",
                            width: "100%",
                            boxSizing: "border-box",
                            flex: "1 1 0%",
                          }}
                        >
                          {/* Avatar */}
                          {coachUser.user_image ? (
                            <Box
                              component="img"
                              src={coachUser.user_image}
                              alt={coachName}
                              sx={{
                                width: 50,
                                height: 50,
                                borderRadius: "50%",
                                objectFit: "cover",
                                mr: 2,
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 50,
                                height: 50,
                                borderRadius: "50%",
                                backgroundColor: "#9c27b0",
                                color: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 600,
                                fontSize: "1.1rem",
                                mr: 2,
                              }}
                            >
                              {coachInitials}
                            </Box>
                          )}

                          {/* Coach Info */}
                          <Box sx={{ 
                            flex: 1, 
                            minWidth: 0, 
                            display: "flex", 
                            flexDirection: "column", 
                            justifyContent: "center",
                            alignSelf: "stretch",
                          }}>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 500,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: { xs: "normal", sm: "nowrap" },
                                wordBreak: { xs: "break-word", sm: "normal" },
                                mb: 0.5,
                                lineHeight: 1.4,
                                fontSize: { xs: "0.875rem", sm: "1rem" },
                                maxWidth: "100%",
                              }}
                            >
                              {coachName}
                            </Typography>
                            <Chip
                              label={t("coach") || "Coach"}
                              size="small"
                              sx={{ 
                                height: 18, 
                                fontSize: "0.65rem", 
                                width: "fit-content",
                                lineHeight: 1,
                                mt: 0.25,
                                backgroundColor: "#9c27b0",
                                color: "#fff",
                              }}
                            />
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
                )}
              </Box>
            ) : (
              <Box sx={{ mb: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("noCoachesInGroup") || "No coaches in this group"}
                </Typography>
              </Box>
            )}

            {!loading &&
              allPlayers.length > 0 &&
              qTrim.length === 1 &&
              totalFilteredPlayers === 0 &&
              !showInvitePanel && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t("searchPlayersMinChars") ||
                    "Type at least 2 characters to invite a new player if nobody matches."}
                </Typography>
              )}

            {!loading &&
              allPlayers.length > 0 &&
              qTrim.length >= 2 &&
              totalFilteredPlayers === 0 &&
              !canInvitePlayer && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t("noPlayersMatchSearch") || "No players match this search."}
                </Typography>
              )}

            {/* Present Players Section */}
            {presentPlayers.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  {t("presentPlayers") || "Present Players"} ({presentPlayers.length})
                </Typography>
                <Grid container spacing={2} sx={{ width: "100%", alignItems: "stretch", display: "flex", flexWrap: "wrap" }}>
                  {presentPlayers.map((player) => (
                    <Grid 
                      item 
                      xs={12} 
                      sm={6}
                      key={player.player_id}
                      sx={{
                        display: "flex",
                        minHeight: 90,
                        flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 16px)", md: "1 1 calc(50% - 16px)" },
                        maxWidth: { xs: "100%", sm: "calc(50% - 16px)", md: "calc(50% - 16px)" },
                      }}
                    >
                      <PlayerCard
                        player={player}
                        status="present"
                        onRemove={() =>
                          hasPermission("PLAYER_REMOVE_FROM_SESSION") &&
                          handlePresenceChange(player, false)
                        }
                        processing={processing[player.player_id]}
                        getPlayerName={getPlayerName}
                        getPlayerInitials={getPlayerInitials}
                        hasPermission={hasPermission}
                        t={t}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Unmarked Players Section */}
            {unmarkedPlayers.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  {t("unmarkedPlayers") || "Unmarked Players"} ({unmarkedPlayers.length})
                </Typography>
                <Grid container spacing={2} sx={{ width: "100%", alignItems: "stretch", display: "flex", flexWrap: "wrap" }}>
                  {unmarkedPlayers.map((player) => (
                    <Grid 
                      item 
                      xs={12} 
                      sm={6}
                      key={player.player_id}
                      sx={{
                        display: "flex",
                        minHeight: 90,
                        flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 16px)", md: "1 1 calc(50% - 16px)" },
                        maxWidth: { xs: "100%", sm: "calc(50% - 16px)", md: "calc(50% - 16px)" },
                      }}
                    >
                      <PlayerCard
                        player={player}
                        status="unmarked"
                        onAdd={() =>
                          hasPermission("PLAYER_ADD_TO_SESSION") &&
                          handlePresenceChange(player, true)
                        }
                        onRemove={() =>
                          hasPermission("PLAYER_REMOVE_FROM_SESSION") &&
                          handlePresenceChange(player, false)
                        }
                        processing={processing[player.player_id]}
                        getPlayerName={getPlayerName}
                        getPlayerInitials={getPlayerInitials}
                        hasPermission={hasPermission}
                        t={t}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Absent Players Section */}
            {absentPlayers.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  {t("absentPlayers") || "Absent Players"} ({absentPlayers.length})
                </Typography>
                <Grid container spacing={2} sx={{ width: "100%", alignItems: "stretch", display: "flex", flexWrap: "wrap" }}>
                  {absentPlayers.map((player) => (
                    <Grid 
                      item 
                      xs={12} 
                      sm={6}
                      key={player.player_id}
                      sx={{
                        display: "flex",
                        minHeight: 90,
                        flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 16px)", md: "1 1 calc(50% - 16px)" },
                        maxWidth: { xs: "100%", sm: "calc(50% - 16px)", md: "calc(50% - 16px)" },
                      }}
                    >
                      <PlayerCard
                        player={player}
                        status="absent"
                        onAdd={() =>
                          hasPermission("PLAYER_ADD_TO_SESSION") &&
                          handlePresenceChange(player, true)
                        }
                        processing={processing[player.player_id]}
                        getPlayerName={getPlayerName}
                        getPlayerInitials={getPlayerInitials}
                        hasPermission={hasPermission}
                        t={t}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Empty State */}
            {allPlayers.length === 0 && (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  color: "text.secondary",
                }}
              >
                <Typography variant="body1">
                  {t("noPlayersInGroup") || "No players in this group"}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          {t("close") || "Close"}
        </Button>
      </DialogActions>
    </Dialog>

      <Dialog
        open={tablePickerOpen}
        onClose={() => {
          if (thirdAssigning) return;
          setTablePickerOpen(false);
          setPendingThirdPlayer(null);
          setThirdFlowMessage(null);
          setThirdPickerRows([]);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, bgcolor: "background.paper", color: "text.primary" } }}
      >
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "text.secondary" }}>
            {t("tablesConfigurationSubtitle") || "Tables configuration"}
          </Typography>
          {pendingThirdPlayer && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {(t("assignThirdPlayerLabel") || "Add as 3rd player") + ": "}
              <strong>{getPlayerName(pendingThirdPlayer)}</strong>
            </Typography>
          )}
          {thirdFlowMessage && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setThirdFlowMessage(null)}>
              {thirdFlowMessage}
            </Alert>
          )}
          {thirdPickerRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("noTableReadyForThirdPlayer") ||
                "No table has two players with a free third seat."}
            </Typography>
          ) : (
            <List dense disablePadding>
              {thirdPickerRows.map((tbl) => (
                <ListItemButton
                  key={tbl.table_id || tbl.table_number}
                  disabled={thirdAssigning}
                  onClick={() => assignThirdPlayerToTable(tbl)}
                  sx={{ borderRadius: 1, mb: 0.5, border: "1px solid", borderColor: "divider" }}
                >
                  <ListItemText primary={formatTableSummaryLine(tbl)} primaryTypographyProps={{ variant: "body2" }} />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setTablePickerOpen(false);
              setPendingThirdPlayer(null);
              setThirdFlowMessage(null);
              setThirdPickerRows([]);
            }}
            disabled={thirdAssigning}
            color="inherit"
          >
            {t("cancel") || "Cancel"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Compact player row for bottom-sheet presentation
const CompactPlayerRow = ({
  player,
  status,
  onAdd,
  onRemove,
  processing,
  getPlayerName,
  getPlayerInitials,
  hasPermission,
  t,
}) => {
  const metaParts = [];
  if (player.classement || player.fltt_classement) {
    metaParts.push(player.classement || player.fltt_classement);
  }
  const points = parseFloat(player.ranking_points || player.fltt_ranking_points || 0);
  if (!isNaN(points) && points > 0) {
    metaParts.push(`${points.toFixed(2)} ${t("points") || "pts"}`);
  }
  if (isGuestPlayer(player) && player.user_email) {
    metaParts.push(player.user_email);
  }

  return (
    <div className={`lt-pp-row lt-pp-row--${status}`}>
      <span className="lt-pp-row__avatar">
        {player.user_image ? (
          <img src={player.user_image} alt="" />
        ) : (
          getPlayerInitials(player)
        )}
      </span>
      <div className="lt-pp-row__info">
        <span className="lt-pp-row__name">{getPlayerName(player)}</span>
        {metaParts.length > 0 ? (
          <span className="lt-pp-row__meta">{metaParts.join(" · ")}</span>
        ) : null}
        {isGuestPlayer(player) ? (
          <span className="lt-pp-row__badge">{t("guest") || "Guest"}</span>
        ) : player.is_demo_simulation ? (
          <span className="lt-pp-row__badge">
            {t("demoSimulationBadge", "Club roster")}
          </span>
        ) : player.is_demo_walkin ? (
          <span className="lt-pp-row__badge">
            {t("demoWalkInBadge", "Phone join")}
          </span>
        ) : player.isActive === false ? (
          <span className="lt-pp-row__badge">{t("inactive") || "Inactive"}</span>
        ) : null}
        {!player.user_id ? (
          <span className="lt-pp-row__badge">{t("notRegistered") || "Not registered"}</span>
        ) : null}
      </div>
      <div className="lt-pp-row__actions">
        {processing ? (
          <CircularProgress size={22} />
        ) : (
          <>
            {status === "present" ? (
              <button
                type="button"
                className="lt-pp-row__btn lt-pp-row__btn--remove"
                onClick={onRemove}
                disabled={!hasPermission("PLAYER_REMOVE_FROM_SESSION")}
                title={t("markAbsent") || "Mark as absent"}
                aria-label={t("markAbsent") || "Mark as absent"}
              >
                <PersonRemoveIcon fontSize="small" />
              </button>
            ) : null}
            {status === "absent" ? (
              <button
                type="button"
                className="lt-pp-row__btn lt-pp-row__btn--add"
                onClick={onAdd}
                disabled={!hasPermission("PLAYER_ADD_TO_SESSION")}
                title={t("markPresent") || "Mark as present"}
                aria-label={t("markPresent") || "Mark as present"}
              >
                <PersonAddIcon fontSize="small" />
              </button>
            ) : null}
            {status === "unmarked" ? (
              <>
                <button
                  type="button"
                  className="lt-pp-row__btn lt-pp-row__btn--add"
                  onClick={onAdd}
                  disabled={!hasPermission("PLAYER_ADD_TO_SESSION")}
                  title={t("markPresent") || "Mark as present"}
                  aria-label={t("markPresent") || "Mark as present"}
                >
                  <PersonAddIcon fontSize="small" />
                </button>
                {hasPermission("PLAYER_REMOVE_FROM_SESSION") ? (
                  <button
                    type="button"
                    className="lt-pp-row__btn lt-pp-row__btn--remove"
                    onClick={onRemove}
                    title={t("markAbsent") || "Mark as absent"}
                    aria-label={t("markAbsent") || "Mark as absent"}
                  >
                    <PersonRemoveIcon fontSize="small" />
                  </button>
                ) : null}
              </>
            ) : null}
            {status === "present" ? (
              <span
                className="lt-pp-row__btn lt-pp-row__btn--done"
                aria-hidden="true"
              >
                <CheckCircleOutlineIcon fontSize="small" />
              </span>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

// Player Card Component
const PlayerCard = ({
  player,
  status,
  onAdd,
  onRemove,
  processing,
  getPlayerName,
  getPlayerInitials,
  hasPermission,
  t,
}) => {
  const getStatusColor = () => {
    switch (status) {
      case "present":
        return "#4caf50";
      case "absent":
        return "#f44336";
      default:
        return "#ff9800";
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        p: { xs: 1.5, sm: 2 },
        border: `2px solid ${getStatusColor()}`,
        borderRadius: 2,
        bgcolor: "background.paper",
        color: "text.primary",
        minHeight: { xs: 80, sm: 90 },
        height: "100%",
        width: "100%",
        boxSizing: "border-box",
        flex: "1 1 0%",
        gap: { xs: 1, sm: 2 },
      }}
    >
      {/* Avatar */}
      {player.user_image ? (
        <Box
          component="img"
          src={player.user_image}
          alt={getPlayerName(player)}
          sx={{
            width: { xs: 45, sm: 50 },
            height: { xs: 45, sm: 50 },
            borderRadius: "50%",
            objectFit: "cover",
            flexShrink: 0,
          }}
        />
      ) : (
        <Box
          sx={{
            width: { xs: 45, sm: 50 },
            height: { xs: 45, sm: 50 },
            borderRadius: "50%",
            backgroundColor: "#339AF0",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
            fontSize: { xs: "0.9rem", sm: "1.1rem" },
            flexShrink: 0,
          }}
        >
          {getPlayerInitials(player)}
        </Box>
      )}

      {/* Player Info */}
      <Box sx={{ 
        flex: 1, 
        minWidth: 0, 
        display: "flex", 
        flexDirection: "column", 
        justifyContent: "center",
        alignSelf: "stretch",
      }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            color: "text.primary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: { xs: "normal", sm: "nowrap" },
            wordBreak: { xs: "break-word", sm: "normal" },
            mb: 0.5,
            lineHeight: 1.4,
            fontSize: { xs: "0.875rem", sm: "1rem" },
            maxWidth: "100%",
          }}
        >
          {getPlayerName(player)}
        </Typography>
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column", 
          gap: 0.25,
        }}>
          {(player.classement || player.fltt_classement) && (
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
              {player.classement || player.fltt_classement}
            </Typography>
          )}
          {(() => {
            const points = parseFloat(player.ranking_points || player.fltt_ranking_points || 0);
            return !isNaN(points) && points > 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                {points.toFixed(2)} {t("points") || "pts"}
              </Typography>
            ) : null;
          })()}
          {isGuestPlayer(player) && player.user_email && (
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
              {player.user_email}
            </Typography>
          )}
          {isGuestPlayer(player) && (
            <Chip
              label={t("guest") || "Guest"}
              size="small"
              color="info"
              sx={{
                height: 18,
                fontSize: "0.65rem",
                width: "fit-content",
                lineHeight: 1,
                mt: 0.25,
              }}
            />
          )}
          {player.is_demo_simulation && !isGuestPlayer(player) && (
            <Chip
              label={t("demoSimulationBadge", "Club roster")}
              size="small"
              color="primary"
              variant="outlined"
              sx={{
                height: 18,
                fontSize: "0.65rem",
                width: "fit-content",
                lineHeight: 1,
                mt: 0.25,
              }}
            />
          )}
          {player.isActive === false && !isGuestPlayer(player) && (
            <Chip
              label={t("inactive") || "Inactive"}
              size="small"
              color="default"
              sx={{
                height: 18,
                fontSize: "0.65rem",
                width: "fit-content",
                lineHeight: 1,
                mt: 0.25,
              }}
            />
          )}
          {!player.user_id && (
            <Chip
              label={t("notRegistered") || "Not registered"}
              size="small"
              sx={{ 
                height: 18, 
                fontSize: "0.65rem", 
                width: "fit-content",
                lineHeight: 1,
                mt: 0.25,
              }}
            />
          )}
        </Box>
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", gap: { xs: 0.5, sm: 1 }, alignItems: "center", flexShrink: 0 }}>
        {processing ? (
          <CircularProgress size={24} />
        ) : (
          <>
            {status === "present" && (
              <>
                <IconButton
                  size="small"
                  onClick={onRemove}
                  disabled={!hasPermission("PLAYER_REMOVE_FROM_SESSION")}
                  color="error"
                  title={t("markAbsent") || "Mark as absent"}
                >
                  <PersonRemoveIcon />
                </IconButton>
                <IconButton
                  size="small"
                  disabled
                  color="success"
                  title={t("present") || "Present"}
                >
                  <CheckCircleOutlineIcon />
                </IconButton>
              </>
            )}
            {status === "absent" && (
              <IconButton
                size="small"
                onClick={onAdd}
                disabled={!hasPermission("PLAYER_ADD_TO_SESSION")}
                color="success"
                title={t("markPresent") || "Mark as present"}
              >
                <PersonAddIcon />
              </IconButton>
            )}
            {status === "unmarked" && (
              <>
                <IconButton
                  size="small"
                  onClick={onAdd}
                  disabled={!hasPermission("PLAYER_ADD_TO_SESSION")}
                  color="success"
                  title={t("markPresent") || "Mark as present"}
                >
                  <PersonAddIcon />
                </IconButton>
                {hasPermission("PLAYER_REMOVE_FROM_SESSION") && (
                  <IconButton
                    size="small"
                    onClick={onRemove}
                    color="error"
                    title={t("markAbsent") || "Mark as absent"}
                  >
                    <PersonRemoveIcon />
                  </IconButton>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default PlayerPresenceDialog;

